import { create } from 'zustand';
import { OnBoardingData, OnBoardingWorkData } from '../dto/EntityRepository';
import { setItemToSessionStorage, removeItemFromSessionStorage, ONBOARDING_KEYS } from '../util/localStorage/useSessionStorage';
import { saveFileToIndexedDB, clearAllFilesFromIndexedDB } from '../util/localStorage/useIndexedDB';

interface OnBoardState {
  onBoardingData: OnBoardingData | null;
  activeWork: OnBoardingWorkData | null;
  setOnBoardingData: (value: OnBoardingData) => void;
  setActiveWork: (work: OnBoardingWorkData | null) => void;
  updateOnBoardingData: (updates: Partial<OnBoardingData>) => void;
  clearOnBoardingData: () => void;
}

export const useOnBoardStore = create<OnBoardState>((set, get) => ({
  onBoardingData: null,
  activeWork: null,
  setOnBoardingData: (value) => set({ onBoardingData: value }),
  setActiveWork: (work) => set({ activeWork: work }),
  updateOnBoardingData: (updates) => {
    const currentData = get().onBoardingData;
    if (!currentData) return;
    
    const newData = { ...currentData, ...updates };
    set({ onBoardingData: newData });
    
    // OnBoardingData에서 File 객체들을 IndexedDB에 저장하고 ID로 변환
    const serializeOnboardingData = async (data: OnBoardingData): Promise<any> => {
      const serialized: any = { ...data };

      if (data.works && data.works.length > 0) {
        serialized.works = await Promise.all(
          data.works.map(async (work: OnBoardingWorkData) => {
            const base: any = { ...work };
            if (work.file instanceof File) {
              const fileId = await saveFileToIndexedDB(work.file);
              return {
                ...base,
                fileId,
                file: null,
              };
            }
            return base;
          })
        );
      }

      return serialized;
    };
    
    // 비동기로 SessionStorage에 저장
    serializeOnboardingData(newData).then(serialized => {
      setItemToSessionStorage(ONBOARDING_KEYS.DATA, serialized);
    }).catch(error => {
      console.error('Failed to save onboarding data:', error);
    });
  },
  clearOnBoardingData: async () => {
    try {
      // 1. Zustand store에서 데이터 제거
      set({ onBoardingData: null, activeWork: null });
      
      // 2. SessionStorage에서 OnBoarding 관련 데이터 제거
      removeItemFromSessionStorage(ONBOARDING_KEYS.DATA);
      removeItemFromSessionStorage(ONBOARDING_KEYS.STEP);
      
      // 3. IndexedDB에서 모든 파일 데이터 제거
      await clearAllFilesFromIndexedDB();
    } catch (error) {
      console.error('Failed to clear onboarding data:', error);
    }
  },
}));